<template>
  <div class="home detail-actions-page">
    <div class="px-8 py-4">
      <!-- Header -->
      <div class="detail-page-header mb-4">
        <button type="button" class="btn btn-link text-decoration-none detail-page-back" @click="$router.go(-1)" style="padding: 10px 0">
          <AppIcon name="bi-arrow-left" class="bi bi-arrow-left me-2" />{{$t('back')}}
        </button>
        <div class="detail-page-title-row d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
          <h3 class="detail-page-title mb-0">{{ $t('cash_register') }} #{{ register.reference }}</h3>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center" style="padding:2rem">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Cash Register Info -->
      <div v-else-if="register && register.id">
        <div class="row">
          <!-- Left Column: Register Details -->
          <div class="col-md-8">
            <div class="card mb-4">
              <div class="card-header bg-white">
                <h6 class="mb-0"><AppIcon name="bi-cash-stack" class="bi bi-cash-stack me-2" />{{ $t('cash_register_summary') }}</h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <small class="text-muted d-block">{{ $t('opened_at') }}</small>
                    <strong>{{ formatDateTime(register.opened_at) }}</strong>
                  </div>
                  <div class="col-md-6 mb-3">
                    <small class="text-muted d-block">{{ $t('closed_at') }}</small>
                    <strong>{{ register.closed_at ? formatDateTime(register.closed_at) : '-' }}</strong>
                  </div>
                  <div class="col-md-6 mb-3">
                    <small class="text-muted d-block">{{ $t('cashier') }}</small>
                    <strong>{{ register?.cashier?.name || register?.cashier?.email || '-' }}</strong>
                  </div>
                  <div class="col-md-6 mb-3">
                    <small class="text-muted d-block">{{ $t('opening_balance') }}</small>
                    <h5 class="text-primary mb-0">{{ formatMoney(register.opening_balance) }}</h5>
                  </div>
                  <div class="col-md-6 mb-3">
                    <small class="text-muted d-block">{{ $t('closing_balance') }}</small>
                    <h5 class="text-success mb-0">{{ register.closing_balance ? formatMoney(register.closing_balance) : '-' }}</h5>
                  </div>
                  <div class="col-md-6 mb-3">
                    <small class="text-muted d-block">{{ $t('total_sales') }}</small>
                    <h5 class="text-info mb-0">{{ formatMoney(totalSales) }}</h5>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Notes Card -->
            <div v-if="register.notes" class="card mb-4 border-start border-4 border-info">
              <div class="card-header bg-white border-0">
                <h6 class="mb-0 text-info fw-bold"><AppIcon name="bi-chat-left-text" class="bi bi-chat-left-text me-2" />{{ $t('session_notes') }}</h6>
              </div>
              <div class="card-body pt-0">
                <p class="mb-0 text-muted fs-6">{{ register.notes }}</p>
              </div>
            </div>

            <!-- Transactions Card -->
            <div class="card">
              <div class="card-header bg-white">
                <h6 class="mb-0"><AppIcon name="bi-receipt" class="bi bi-receipt me-2" />{{ $t('transactions') }}</h6>
              </div>
              <div class="card-body table-responsive">
                <table class="table mb-0 table-striped small">
                  <thead>
                    <tr class="text-secondary">
                      <th>{{ $t('reference') }}</th>
                      <th>{{ $t('total_amount') }}</th>
                      <th>{{ $t('status') }}</th>
                      <th>{{ $t('created_at') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="transaction in register.pos" :key="transaction.id">
                      <td>
                        <router-link :to="{ path: `/pos-transactions/${transaction.id}` }" style="cursor: pointer;">{{ transaction.reference }}</router-link>
                      </td>
                      <td>{{ formatMoney(transaction.total_amount) }}</td>
                      <td>
                        <span :class="transaction.payment_status === 'paid' ? 'badge bg-success' : 'badge bg-info'">
                          {{ $t(transaction.payment_status) }}
                        </span>
                      </td>
                      <td>{{ formatDateTime(transaction.created_at) }}</td>
                    </tr>
                    <tr v-if="!register.pos || register.pos.length === 0">
                      <td class="text-center text-muted" colspan="4">{{ $t('no_transactions_found') }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Right Column: Summary Stats -->
          <div class="col-md-4">
            <div class="card">
              <div class="card-header bg-white">
                <h6 class="mb-0"><AppIcon name="bi-bar-chart" class="bi bi-bar-chart me-2" />{{ $t('summary') }}</h6>
              </div>
              <div class="card-body">
                <div class="mb-3">
                  <small class="text-muted d-block">{{ $t('total_transactions') }}</small>
                  <h4 class="mb-0">{{ register.pos ? register.pos.length : 0 }}</h4>
                </div>
                <div class="mb-3">
                  <small class="text-muted d-block">{{ $t('total_sales') }}</small>
                  <h4 class="text-primary mb-0">{{ formatMoney(totalSales) }}</h4>
                </div>
                <div>
                  <small class="text-muted d-block">{{ $t('status') }}</small>
                  <span class="badge" :class="register.closed_at ? 'bg-secondary' : 'bg-success'">
                    {{ register.closed_at ? $t('closed') : $t('open') }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  


</template>

<script>
import helper from '../../helper';

export default {
  name: 'PosCashRegisterDetails',
  data() {
    return {
      register: {},
      transactions: [],
      totalSales: 0,
      loading: false
    };
  },
  computed: {
    business() {return this.$store.getters.BUSINESS;},
    helper(){ return helper},
  },
  methods: {
    formatMoney(number) { return helper.formatMoney(number);},
    async fetchRegisterDetails() {
      try {
        this.loading = true;
        const registerId = this.$route.params.id;
        const response = await helper.customGet(`${process.env.VUE_APP_API}/cash-registers/${registerId}`);
        this.register = response.data;
        this.transactions = response.data.pos;
        this.totalSales = this.transactions.reduce((sum, transaction) => parseFloat(sum) + parseFloat(transaction.total_amount), 0);
        this.loading = false;
      } catch (error) {
        this.loading = false;
      }
    },
    formatDateTime(date) {
      if (!date) return '';
      const dt = new Date(date);
      return dt.toLocaleString();
    }
  },
  mounted() {
    window.mixpanel.track("pos_cash_register_details");

    if(helper.checkAccess('view_pos_cash_registers') == -1) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_pos_cash_registers') == -2) return this.$router.push({path : '/subscribe'})

    this.fetchRegisterDetails();
  }
};
</script>

<style scoped>
.cash-register-details {
  padding: 20px;
}
.badge {
  font-size: 1rem;
  padding: 0.5em 1em;
}
.table-borderless>:not(caption)>*>* {
  padding: 5px !important;
}
</style>
